import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Grid,
    MenuItem,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword, updateProfile } from "firebase/auth";
import AdminLayout from "./AdminLayout";
import { auth } from "../firebase/config";
import { useAuth } from "../hooks/useAuth";
import validators from "../utils/validators";

const sanitizeText = (value) => String(value || "").replace(/\s+/g, " ").trimStart();

const ACADEMIC_YEARS = [
    "1st Year",
    "2nd Year",
    "3rd Year",
    "4th Year",
    "5th Year",
    "Postgraduate",
    "Faculty",
    "Other"
];

export default function SettingsPage() {
    const { user, userProfile, updateUserProfile } = useAuth();
    const [profileForm, setProfileForm] = useState({
        username: "",
        firstName: "",
        lastName: "",
        course: "",
        academicYear: "",
        phone: "",
        personalDetails: ""
    });
    const [initialProfile, setInitialProfile] = useState(profileForm);
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
    });
    const [profileSubmitting, setProfileSubmitting] = useState(false);
    const [passwordSubmitting, setPasswordSubmitting] = useState(false);
    const [profileMessage, setProfileMessage] = useState({ type: "", text: "" });
    const [passwordMessage, setPasswordMessage] = useState({ type: "", text: "" });

    useEffect(() => {
        const next = {
            username: userProfile?.username || userProfile?.displayName || `${userProfile?.firstName || ""} ${userProfile?.lastName || ""}`.trim(),
            firstName: userProfile?.firstName || "",
            lastName: userProfile?.lastName || "",
            course: userProfile?.course || "",
            academicYear: userProfile?.academicYear || userProfile?.year || "",
            phone: userProfile?.phone || userProfile?.mobileNo || "",
            personalDetails: userProfile?.personalDetails || userProfile?.bio || ""
        };
        setProfileForm(next);
        setInitialProfile(next);
    }, [userProfile]);

    const profileErrors = useMemo(() => {
        const errors = {};
        if (!validators.isNotEmpty(profileForm.username) || !validators.minLength(profileForm.username, 3)) {
            errors.username = "Username must be at least 3 characters";
        }
        if (!validators.isNotEmpty(profileForm.firstName)) {
            errors.firstName = "First name is required";
        }
        if (!validators.isNotEmpty(profileForm.lastName)) {
            errors.lastName = "Last name is required";
        }
        if (!validators.isNotEmpty(profileForm.course)) {
            errors.course = "Course enrollment is required";
        }
        if (!validators.isNotEmpty(profileForm.academicYear)) {
            errors.academicYear = "Academic year is required";
        }
        if (profileForm.phone && !validators.isValidPhone(profileForm.phone)) {
            errors.phone = "Phone number must be 10 digits";
        }
        if (!validators.maxLength(profileForm.personalDetails, 300)) {
            errors.personalDetails = "Personal details must be under 300 characters";
        }
        return errors;
    }, [profileForm]);

    const passwordErrors = useMemo(() => {
        const errors = {};
        if (passwordForm.newPassword || passwordForm.confirmPassword || passwordForm.currentPassword) {
            if (!passwordForm.currentPassword) {
                errors.currentPassword = "Current password is required";
            }
            if (!validators.isStrongPassword(passwordForm.newPassword || "")) {
                errors.newPassword = "New password must be 8+ chars with one uppercase and one number";
            }
            if (passwordForm.confirmPassword !== passwordForm.newPassword) {
                errors.confirmPassword = "Password confirmation does not match";
            }
            if (passwordForm.currentPassword && passwordForm.newPassword && passwordForm.currentPassword === passwordForm.newPassword) {
                errors.newPassword = "New password must be different from current password";
            }
        }
        return errors;
    }, [passwordForm]);

    const hasProfileChanges = useMemo(() => {
        return JSON.stringify(profileForm) !== JSON.stringify(initialProfile);
    }, [profileForm, initialProfile]);

    const handleProfileInput = (field, value) => {
        setProfileForm((prev) => ({
            ...prev,
            [field]: field === "academicYear" ? value : sanitizeText(value)
        }));
    };

    const handlePasswordInput = (field, value) => {
        setPasswordForm((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleResetProfile = () => {
        setProfileForm(initialProfile);
        setProfileMessage({ type: "", text: "" });
    };

    const handleSaveProfile = async () => {
        if (Object.keys(profileErrors).length > 0) {
            setProfileMessage({ type: "error", text: "Please resolve validation errors before saving." });
            return;
        }
        try {
            setProfileSubmitting(true);
            setProfileMessage({ type: "", text: "" });
            const payload = {
                username: sanitizeText(profileForm.username).trim(),
                firstName: sanitizeText(profileForm.firstName).trim(),
                lastName: sanitizeText(profileForm.lastName).trim(),
                course: sanitizeText(profileForm.course).trim(),
                academicYear: profileForm.academicYear,
                phone: String(profileForm.phone || "").replace(/\D/g, "").slice(0, 10),
                personalDetails: sanitizeText(profileForm.personalDetails).trim()
            };
            await updateUserProfile(payload);
            if (auth.currentUser && payload.username) {
                await updateProfile(auth.currentUser, { displayName: payload.username });
            }
            setInitialProfile(payload);
            setProfileForm(payload);
            setProfileMessage({ type: "success", text: "Settings updated successfully." });
        } catch (error) {
            setProfileMessage({ type: "error", text: error.message || "Failed to update settings." });
        } finally {
            setProfileSubmitting(false);
        }
    };

    const handleUpdatePassword = async () => {
        if (Object.keys(passwordErrors).length > 0) {
            setPasswordMessage({ type: "error", text: "Please resolve password validation errors." });
            return;
        }
        if (!passwordForm.currentPassword && !passwordForm.newPassword && !passwordForm.confirmPassword) {
            setPasswordMessage({ type: "error", text: "Please enter password fields to update password." });
            return;
        }
        try {
            setPasswordSubmitting(true);
            setPasswordMessage({ type: "", text: "" });
            if (!user?.email) {
                throw new Error("No email associated with this account.");
            }
            const credential = EmailAuthProvider.credential(user.email, passwordForm.currentPassword);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, passwordForm.newPassword);
            setPasswordForm({
                currentPassword: "",
                newPassword: "",
                confirmPassword: ""
            });
            setPasswordMessage({ type: "success", text: "Password updated successfully." });
        } catch (error) {
            const text = error?.code === "auth/wrong-password"
                ? "Current password is incorrect."
                : error?.message || "Failed to update password.";
            setPasswordMessage({ type: "error", text });
        } finally {
            setPasswordSubmitting(false);
        }
    };

    const handleResetPasswordForm = () => {
        setPasswordForm({
            currentPassword: "",
            newPassword: "",
            confirmPassword: ""
        });
        setPasswordMessage({ type: "", text: "" });
    };

    return (
        <AdminLayout title="Settings">
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#003366", mb: 0.5 }}>
                    Account Settings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Update your profile information and account security settings.
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: "#003366", mb: 2 }}>
                                    Profile Information
                                </Typography>
                                {profileMessage.text && (
                                    <Alert severity={profileMessage.type || "info"} sx={{ mb: 2 }}>
                                        {profileMessage.text}
                                    </Alert>
                                )}
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Username"
                                            value={profileForm.username}
                                            onChange={(e) => handleProfileInput("username", e.target.value)}
                                            error={!!profileErrors.username}
                                            helperText={profileErrors.username || "Shown publicly in your profile"}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="First Name"
                                            value={profileForm.firstName}
                                            onChange={(e) => handleProfileInput("firstName", e.target.value)}
                                            error={!!profileErrors.firstName}
                                            helperText={profileErrors.firstName || "Required"}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Last Name"
                                            value={profileForm.lastName}
                                            onChange={(e) => handleProfileInput("lastName", e.target.value)}
                                            error={!!profileErrors.lastName}
                                            helperText={profileErrors.lastName || "Required"}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Course Enrollment"
                                            value={profileForm.course}
                                            onChange={(e) => handleProfileInput("course", e.target.value)}
                                            error={!!profileErrors.course}
                                            helperText={profileErrors.course || "Required"}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Academic Year"
                                            value={profileForm.academicYear}
                                            onChange={(e) => handleProfileInput("academicYear", e.target.value)}
                                            error={!!profileErrors.academicYear}
                                            helperText={profileErrors.academicYear || "Required"}
                                        >
                                            {ACADEMIC_YEARS.map((year) => (
                                                <MenuItem key={year} value={year}>
                                                    {year}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            value={userProfile?.email || ""}
                                            disabled
                                            helperText="Email is managed by your sign-in account"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Phone"
                                            value={profileForm.phone}
                                            onChange={(e) => handleProfileInput("phone", e.target.value)}
                                            error={!!profileErrors.phone}
                                            helperText={profileErrors.phone || "10 digit mobile number"}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Personal Details"
                                            multiline
                                            rows={3}
                                            value={profileForm.personalDetails}
                                            onChange={(e) => handleProfileInput("personalDetails", e.target.value)}
                                            error={!!profileErrors.personalDetails}
                                            helperText={profileErrors.personalDetails || "Optional bio or additional profile details"}
                                        />
                                    </Grid>
                                </Grid>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSaveProfile}
                                        disabled={profileSubmitting || !hasProfileChanges || Object.keys(profileErrors).length > 0}
                                        sx={{ backgroundColor: "#003366", "&:hover": { backgroundColor: "#02264a" } }}
                                    >
                                        {profileSubmitting ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Update Settings"}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={handleResetProfile}
                                        disabled={profileSubmitting || !hasProfileChanges}
                                    >
                                        Cancel Changes
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: "#003366", mb: 2 }}>
                                    Security
                                </Typography>
                                {passwordMessage.text && (
                                    <Alert severity={passwordMessage.type || "info"} sx={{ mb: 2 }}>
                                        {passwordMessage.text}
                                    </Alert>
                                )}
                                <Stack spacing={2}>
                                    <TextField
                                        fullWidth
                                        type="password"
                                        label="Current Password"
                                        value={passwordForm.currentPassword}
                                        onChange={(e) => handlePasswordInput("currentPassword", e.target.value)}
                                        error={!!passwordErrors.currentPassword}
                                        helperText={passwordErrors.currentPassword || "Required for secure password updates"}
                                    />
                                    <TextField
                                        fullWidth
                                        type="password"
                                        label="New Password"
                                        value={passwordForm.newPassword}
                                        onChange={(e) => handlePasswordInput("newPassword", e.target.value)}
                                        error={!!passwordErrors.newPassword}
                                        helperText={passwordErrors.newPassword || "Use at least 8 chars with uppercase and number"}
                                    />
                                    <TextField
                                        fullWidth
                                        type="password"
                                        label="Confirm New Password"
                                        value={passwordForm.confirmPassword}
                                        onChange={(e) => handlePasswordInput("confirmPassword", e.target.value)}
                                        error={!!passwordErrors.confirmPassword}
                                        helperText={passwordErrors.confirmPassword || "Must match new password"}
                                    />
                                </Stack>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={handleUpdatePassword}
                                        disabled={passwordSubmitting || Object.keys(passwordErrors).length > 0}
                                    >
                                        {passwordSubmitting ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Update Password"}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={handleResetPasswordForm}
                                        disabled={passwordSubmitting}
                                    >
                                        Reset
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </AdminLayout>
    );
}
